import { createAnonymizationPipeline, defaultConfig } from '@blindquery/core';
import { appState } from './appState';
import { dom } from './dom';
import { ensureProjectEntityContext } from './entityMaps';
import { t } from './i18n';
import { queueChatForMemory } from './memory';
import { store } from './store';
import { storage } from './storage';
import { updateUI } from './ui';
export const getDefaultSystemPrompt = () => t('modals.settings.systemPrompt.default');
const SETTINGS_GLOBAL_KEY = 'blindquery_settings_global';
const SETTINGS_PROJECT_PREFIX = 'blindquery_settings_project_';
const DEFAULT_NER_CONFIG = {
    persons: true,
    locations: true,
    organizations: false,
    misc: false
};
const getSanitizationOptions = () => [
    {
        key: 'phoneNumbers',
        label: t('modals.settings.sanitization.options.phoneNumbers.label'),
        description: t('modals.settings.sanitization.options.phoneNumbers.description')
    },
    {
        key: 'emails',
        label: t('modals.settings.sanitization.options.emails.label'),
        description: t('modals.settings.sanitization.options.emails.description')
    },
    {
        key: 'creditCards',
        label: t('modals.settings.sanitization.options.creditCards.label'),
        description: t('modals.settings.sanitization.options.creditCards.description')
    },
    {
        key: 'ssns',
        label: t('modals.settings.sanitization.options.ssns.label'),
        description: t('modals.settings.sanitization.options.ssns.description')
    },
    {
        key: 'usernames',
        label: t('modals.settings.sanitization.options.usernames.label'),
        description: t('modals.settings.sanitization.options.usernames.description')
    },
    {
        key: 'ipAddresses',
        label: t('modals.settings.sanitization.options.ipAddresses.label'),
        description: t('modals.settings.sanitization.options.ipAddresses.description')
    },
    {
        key: 'dates',
        label: t('modals.settings.sanitization.options.dates.label'),
        description: t('modals.settings.sanitization.options.dates.description')
    },
    {
        key: 'governmentIds',
        label: t('modals.settings.sanitization.options.governmentIds.label'),
        description: t('modals.settings.sanitization.options.governmentIds.description')
    },
    {
        key: 'bankAccounts',
        label: t('modals.settings.sanitization.options.bankAccounts.label'),
        description: t('modals.settings.sanitization.options.bankAccounts.description')
    },
    {
        key: 'cryptoWallets',
        label: t('modals.settings.sanitization.options.cryptoWallets.label'),
        description: t('modals.settings.sanitization.options.cryptoWallets.description')
    },
    {
        key: 'macAddresses',
        label: t('modals.settings.sanitization.options.macAddresses.label'),
        description: t('modals.settings.sanitization.options.macAddresses.description')
    },
    {
        key: 'deviceIds',
        label: t('modals.settings.sanitization.options.deviceIds.label'),
        description: t('modals.settings.sanitization.options.deviceIds.description')
    },
    {
        key: 'cookies',
        label: t('modals.settings.sanitization.options.cookies.label'),
        description: t('modals.settings.sanitization.options.cookies.description')
    }
];
const getNerOptions = () => [
    {
        key: 'persons',
        label: t('modals.settings.ner.options.persons.label'),
        description: t('modals.settings.ner.options.persons.description')
    },
    {
        key: 'locations',
        label: t('modals.settings.ner.options.locations.label'),
        description: t('modals.settings.ner.options.locations.description')
    },
    {
        key: 'organizations',
        label: t('modals.settings.ner.options.organizations.label'),
        description: t('modals.settings.ner.options.organizations.description')
    },
    {
        key: 'misc',
        label: t('modals.settings.ner.options.misc.label'),
        description: t('modals.settings.ner.options.misc.description')
    }
];
let globalSettings = buildDefaultSettings();
const projectSettingsCache = new Map();
let activeSettings = globalSettings;
function buildDefaultSettings() {
    return {
        systemPrompt: getDefaultSystemPrompt(),
        sanitization: { ...defaultConfig.sanitization },
        ner: { ...DEFAULT_NER_CONFIG },
        semanticMemoryEnabled: true,
        memoryScope: 'project'
    };
}
function cloneSettings(settings) {
    return {
        systemPrompt: settings.systemPrompt,
        sanitization: { ...settings.sanitization },
        ner: { ...settings.ner },
        semanticMemoryEnabled: settings.semanticMemoryEnabled,
        memoryScope: settings.memoryScope
    };
}
function normalizeSettings(settings) {
    if (!settings) {
        return buildDefaultSettings();
    }
    return {
        systemPrompt: settings.systemPrompt?.trim() || getDefaultSystemPrompt(),
        sanitization: {
            ...defaultConfig.sanitization,
            ...settings.sanitization
        },
        ner: {
            ...DEFAULT_NER_CONFIG,
            ...settings.ner
        },
        semanticMemoryEnabled: settings.semanticMemoryEnabled ?? true,
        memoryScope: settings.memoryScope === 'chat' ? 'chat' : 'project'
    };
}
function projectSettingsKey(projectId) {
    return `${SETTINGS_PROJECT_PREFIX}${projectId}`;
}
export async function loadGlobalSettings() {
    const stored = await storage.load(SETTINGS_GLOBAL_KEY);
    globalSettings = normalizeSettings(stored);
}
async function loadProjectSettings(projectId) {
    if (projectSettingsCache.has(projectId)) {
        return;
    }
    const stored = await storage.load(projectSettingsKey(projectId));
    projectSettingsCache.set(projectId, stored ? normalizeSettings(stored) : null);
}
function getProjectSettings(projectId) {
    if (!projectId) {
        return null;
    }
    return projectSettingsCache.get(projectId) || null;
}
function isProjectUsingGlobal() {
    if (!appState.currentProjectId) {
        return true;
    }
    return !projectSettingsCache.get(appState.currentProjectId);
}
function resolveActiveSettings() {
    const projectSettings = getProjectSettings(appState.currentProjectId);
    return projectSettings || globalSettings;
}
async function saveGlobalSettings(settings) {
    await storage.save(SETTINGS_GLOBAL_KEY, settings);
}
async function saveProjectSettings(projectId, settings) {
    if (!settings) {
        storage.remove(projectSettingsKey(projectId));
        return;
    }
    await storage.save(projectSettingsKey(projectId), settings);
}
export function clearProjectSettings(projectId) {
    projectSettingsCache.delete(projectId);
    storage.remove(projectSettingsKey(projectId));
}
function configurePipeline(settings) {
    const config = {
        sanitization: settings.sanitization,
        ner: settings.ner
    };
    const { context, sanitizerPipeline, entityAnonymizer } = createAnonymizationPipeline(config);
    appState.entityContext = context;
    appState.sanitizationPipeline = sanitizerPipeline;
    appState.entityAnonymizer = entityAnonymizer;
}
export function applyActiveSettings() {
    activeSettings = resolveActiveSettings();
    appState.currentSystemPrompt = activeSettings.systemPrompt || getDefaultSystemPrompt();
    appState.memoryEnabled = activeSettings.semanticMemoryEnabled;
    appState.memoryScope = activeSettings.memoryScope;
    configurePipeline(activeSettings);
    void ensureProjectEntityContext(appState.currentProjectId);
    updateUI();
}
function seedCurrentChatMemory() {
    if (!appState.memoryEnabled || !appState.currentProjectId || !appState.currentChatId) {
        return;
    }
    const chat = store.getChat(appState.currentProjectId, appState.currentChatId);
    if (chat) {
        queueChatForMemory(appState.currentProjectId, appState.currentChatId, chat.messages);
    }
}
function renderToggleOptions(container, options, settings, scope, type, disabled) {
    container.innerHTML = '';
    options.forEach(option => {
        const col = document.createElement('div');
        col.className = 'col-12 col-lg-6';
        col.innerHTML = `
      <div class="settings-card">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div class="fw-semibold">${option.label}</div>
            <div class="small text-muted">${option.description}</div>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox">
          </div>
        </div>
      </div>
    `;
        const input = col.querySelector('input');
        const isChecked = type === 'sanitization'
            ? settings.sanitization[option.key]
            : settings.ner[option.key];
        input.checked = Boolean(isChecked);
        input.disabled = disabled;
        input.addEventListener('change', () => {
            void handleToggle(scope, type, option.key, input.checked);
        });
        container.appendChild(col);
    });
}
export function renderSettingsModal() {
    if (!dom.globalSystemPromptInput || !dom.projectSystemPromptInput) {
        return;
    }
    dom.globalSystemPromptInput.value = globalSettings.systemPrompt;
    dom.globalMemoryToggle.checked = globalSettings.semanticMemoryEnabled;
    dom.globalMemoryScope.value = globalSettings.memoryScope;
    dom.globalMemoryScope.disabled = !globalSettings.semanticMemoryEnabled;
    renderToggleOptions(dom.globalSanitizeOptions, getSanitizationOptions(), globalSettings, 'global', 'sanitization', false);
    renderToggleOptions(dom.globalNerOptions, getNerOptions(), globalSettings, 'global', 'ner', false);
    const project = appState.currentProjectId ? store.getProject(appState.currentProjectId) : undefined;
    if (!project) {
        dom.projectSettingsTitle.textContent = t('modals.settings.project.emptyTitle');
        dom.projectSettingsSubtitle.textContent = t('modals.settings.project.emptySubtitle');
        dom.projectUseGlobalToggle.checked = true;
        dom.projectUseGlobalToggle.disabled = true;
        dom.projectSettingsNotice.textContent = t('modals.settings.project.emptyNotice');
        dom.projectSettingsNotice.classList.remove('d-none');
        dom.projectSystemPromptInput.value = globalSettings.systemPrompt;
        dom.projectSystemPromptInput.disabled = true;
        dom.projectMemoryToggle.checked = globalSettings.semanticMemoryEnabled;
        dom.projectMemoryToggle.disabled = true;
        dom.projectMemoryScope.value = globalSettings.memoryScope;
        dom.projectMemoryScope.disabled = true;
        renderToggleOptions(dom.projectSanitizeOptions, getSanitizationOptions(), globalSettings, 'project', 'sanitization', true);
        renderToggleOptions(dom.projectNerOptions, getNerOptions(), globalSettings, 'project', 'ner', true);
        return;
    }
    dom.projectSettingsTitle.textContent = project.name;
    dom.projectSettingsSubtitle.textContent = t('modals.settings.project.subtitle');
    dom.projectUseGlobalToggle.disabled = false;
    const projectSettings = getProjectSettings(project.id);
    const usingGlobal = !projectSettings;
    dom.projectUseGlobalToggle.checked = usingGlobal;
    dom.projectSettingsNotice.textContent = usingGlobal
        ? t('modals.settings.project.noticeGlobal')
        : t('modals.settings.project.noticeCustom');
    dom.projectSettingsNotice.classList.toggle('d-none', !usingGlobal);
    const effectiveSettings = projectSettings || globalSettings;
    dom.projectSystemPromptInput.value = effectiveSettings.systemPrompt;
    dom.projectSystemPromptInput.disabled = usingGlobal;
    dom.projectMemoryToggle.checked = effectiveSettings.semanticMemoryEnabled;
    dom.projectMemoryToggle.disabled = usingGlobal;
    dom.projectMemoryScope.value = effectiveSettings.memoryScope;
    dom.projectMemoryScope.disabled = usingGlobal || !effectiveSettings.semanticMemoryEnabled;
    renderToggleOptions(dom.projectSanitizeOptions, getSanitizationOptions(), effectiveSettings, 'project', 'sanitization', usingGlobal);
    renderToggleOptions(dom.projectNerOptions, getNerOptions(), effectiveSettings, 'project', 'ner', usingGlobal);
}
export async function handleGlobalPromptChange() {
    const prompt = dom.globalSystemPromptInput.value.trim() || getDefaultSystemPrompt();
    globalSettings.systemPrompt = prompt;
    await saveGlobalSettings(globalSettings);
    if (isProjectUsingGlobal()) {
        appState.currentSystemPrompt = prompt;
        applyActiveSettings();
        dom.projectSystemPromptInput.value = prompt;
    }
}
export async function handleProjectPromptChange() {
    if (!appState.currentProjectId || isProjectUsingGlobal()) {
        return;
    }
    const projectSettings = getProjectSettings(appState.currentProjectId);
    if (!projectSettings) {
        return;
    }
    projectSettings.systemPrompt = dom.projectSystemPromptInput.value.trim() || getDefaultSystemPrompt();
    await saveProjectSettings(appState.currentProjectId, projectSettings);
    applyActiveSettings();
}
export async function handleGlobalMemoryToggle() {
    globalSettings.semanticMemoryEnabled = dom.globalMemoryToggle.checked;
    await saveGlobalSettings(globalSettings);
    if (isProjectUsingGlobal()) {
        applyActiveSettings();
    }
    renderSettingsModal();
}
export async function handleGlobalMemoryScopeChange() {
    globalSettings.memoryScope = dom.globalMemoryScope.value === 'chat' ? 'chat' : 'project';
    await saveGlobalSettings(globalSettings);
    if (isProjectUsingGlobal()) {
        applyActiveSettings();
    }
    renderSettingsModal();
}
export async function handleProjectMemoryToggle() {
    if (!appState.currentProjectId || isProjectUsingGlobal()) {
        return;
    }
    const projectSettings = getProjectSettings(appState.currentProjectId);
    if (!projectSettings) {
        return;
    }
    projectSettings.semanticMemoryEnabled = dom.projectMemoryToggle.checked;
    await saveProjectSettings(appState.currentProjectId, projectSettings);
    applyActiveSettings();
    if (projectSettings.semanticMemoryEnabled) {
        seedCurrentChatMemory();
    }
    renderSettingsModal();
}
export async function handleProjectMemoryScopeChange() {
    if (!appState.currentProjectId || isProjectUsingGlobal()) {
        return;
    }
    const projectSettings = getProjectSettings(appState.currentProjectId);
    if (!projectSettings) {
        return;
    }
    projectSettings.memoryScope = dom.projectMemoryScope.value === 'chat' ? 'chat' : 'project';
    await saveProjectSettings(appState.currentProjectId, projectSettings);
    applyActiveSettings();
    renderSettingsModal();
}
async function handleToggle(scope, type, key, enabled) {
    if (scope === 'global') {
        if (type === 'sanitization') {
            globalSettings.sanitization[key] = enabled;
        }
        else {
            globalSettings.ner[key] = enabled;
        }
        await saveGlobalSettings(globalSettings);
        if (isProjectUsingGlobal()) {
            applyActiveSettings();
            if (globalSettings.semanticMemoryEnabled) {
                seedCurrentChatMemory();
            }
        }
        renderSettingsModal();
        return;
    }
    if (!appState.currentProjectId || isProjectUsingGlobal()) {
        return;
    }
    const projectSettings = getProjectSettings(appState.currentProjectId);
    if (!projectSettings) {
        return;
    }
    if (type === 'sanitization') {
        projectSettings.sanitization[key] = enabled;
    }
    else {
        projectSettings.ner[key] = enabled;
    }
    await saveProjectSettings(appState.currentProjectId, projectSettings);
    applyActiveSettings();
}
export async function handleProjectUseGlobalToggle() {
    if (!appState.currentProjectId) {
        return;
    }
    if (dom.projectUseGlobalToggle.checked) {
        projectSettingsCache.set(appState.currentProjectId, null);
        await saveProjectSettings(appState.currentProjectId, null);
    }
    else {
        const existing = getProjectSettings(appState.currentProjectId);
        const nextSettings = existing ? cloneSettings(existing) : cloneSettings(globalSettings);
        projectSettingsCache.set(appState.currentProjectId, nextSettings);
        await saveProjectSettings(appState.currentProjectId, nextSettings);
    }
    applyActiveSettings();
    renderSettingsModal();
}
export async function refreshProjectSettingsContext() {
    if (appState.currentProjectId) {
        await loadProjectSettings(appState.currentProjectId);
    }
    applyActiveSettings();
    renderSettingsModal();
}
